/**
 * ToolsPage — /agents/tools
 * The tool catalog on the ListPage template. Demo page: /tables/data-tables (toolbar with search, quick filter and
 * scoped selects above one table). Search is local and also asks the marketplace (entity-management §1.3, §5).
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, Sparkles } from 'lucide-react';
import { listToolsOptions } from '../../generated/api/@tanstack/react-query.gen.js';
import type { ToolSummary } from '../../generated/api/types.gen.js';
import {
  Badge,
  Callout,
  DataTable,
  EmptyState,
  FilterPills,
  SearchBar,
  Toolbar,
  describeQueryError,
  type DataTableColumn,
} from '../../components/ui/index.js';
import { ListPage } from '../../components/page/index.js';
import { buttonStyles } from '../../components/tailgrids/core/button.js';
import { Can } from '../../components/auth/Can.js';
import { MarketplaceResults } from '../../components/entity/MarketplaceResults.js';
import { MarketplaceDetailSheet } from '../../components/entity/MarketplaceDetailSheet.js';
import type { MarketplaceListing } from '../../services/api/marketplace.api.js';
import { PAGE_HELP } from '../../help/index.js';
import { FilterSelect, facetOptions, sentenceCase } from '../../components/ui/FilterSelect.js';

type StatusPill = 'active' | 'deprecated' | 'all';

function matchesSearch(tool: ToolSummary, query: string): boolean {
  if (!query) return true;
  return `${tool.name} ${tool.description ?? ''} ${tool.category ?? ''}`.toLowerCase().includes(query);
}

const COLUMNS: DataTableColumn<ToolSummary>[] = [
  {
    key: 'name',
    header: 'Tool',
    width: '34%',
    sortable: true,
    sortValue: (tool) => tool.name,
    render: (tool) => (
      <div className="min-w-0">
        <Link
          to={`/agents/tools/${tool.name}`}
          className={`text-[13px] font-semibold transition-colors hover:text-accent-text ${tool.deprecated ? 'text-muted line-through' : 'text-heading'}`}
        >
          {tool.name}
        </Link>
        <p className="mt-0.5 line-clamp-1 max-w-[420px] text-[11px] text-muted">
          {tool.description || 'No description'}
        </p>
      </div>
    ),
  },
  {
    key: 'category',
    header: 'Category',
    sortable: true,
    sortValue: (tool) => tool.category ?? '',
    render: (tool) =>
      tool.category ? (
        <Badge variant="neutral">{sentenceCase(tool.category)}</Badge>
      ) : (
        <span className="text-faint">None</span>
      ),
  },
  {
    key: 'source',
    header: 'Source',
    sortable: true,
    sortValue: (tool) => tool.sourceLabel,
    render: (tool) => (
      <span className="text-body" title={tool.sourceReference ?? undefined}>
        {tool.sourceLabel}
      </span>
    ),
  },
  {
    key: 'version',
    header: 'Version',
    render: (tool) => <span className="text-muted tabular-nums">{tool.version ? `v${tool.version}` : 'None'}</span>,
  },
  {
    key: 'models',
    header: 'Models',
    align: 'right',
    sortable: true,
    sortValue: (tool) => tool.supportedModels.length,
    render: (tool) => (
      <span className="text-body tabular-nums" title={tool.supportedModels.join(', ')}>
        {tool.supportedModels.length}
      </span>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    render: (tool) => (
      <Badge variant={tool.deprecated ? 'warning' : 'success'} dot>
        {tool.deprecated ? 'Deprecated' : 'Active'}
      </Badge>
    ),
  },
  {
    key: 'usedBy',
    header: 'Used by',
    align: 'right',
    sortable: true,
    sortValue: (tool) => tool.templateCount,
    render: (tool) =>
      tool.templateCount === 0 ? (
        <span className="text-faint">None</span>
      ) : (
        <span
          className="text-body tabular-nums"
          title={`${tool.templateCount} template${tool.templateCount === 1 ? '' : 's'} use this tool`}
        >
          {tool.templateCount} {tool.templateCount === 1 ? 'template' : 'templates'}
        </span>
      ),
  },
];

export const ToolsPage: React.FC = () => {
  const help = PAGE_HELP['tools'];
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusPill>('active');
  const [category, setCategory] = useState('');
  const [source, setSource] = useState('');
  const [marketplaceTarget, setMarketplaceTarget] = useState<MarketplaceListing | null>(null);

  // Deprecated tools included: the status pills decide what shows.
  const toolsQuery = useQuery(listToolsOptions({ query: { includeDeprecated: 'true' } }));
  const tools = toolsQuery.data?.data ?? [];

  const query = search.trim().toLowerCase();
  const visible = tools.filter((tool) => {
    if (!matchesSearch(tool, query)) return false;
    if (status === 'active' && tool.deprecated) return false;
    if (status === 'deprecated' && !tool.deprecated) return false;
    if (category && tool.category !== category) return false;
    if (source && tool.sourceLabel !== source) return false;
    return true;
  });

  const pills = [
    { id: 'active', label: 'Active', count: tools.filter((t) => !t.deprecated).length },
    { id: 'deprecated', label: 'Deprecated', count: tools.filter((t) => t.deprecated).length },
    { id: 'all', label: 'All', count: tools.length },
  ];

  const newToolLink = (
    <Can policy="CanManageSkills">
      <Link to="/agents/tools/new" className={buttonStyles({ variant: 'primary', appearance: 'fill', size: 'sm' })}>
        <Plus size={14} aria-hidden="true" />
        New tool
      </Link>
    </Can>
  );

  return (
    <>
      <ListPage
        title="Tools"
        subtitle="The capabilities agents use to act: fetch data, compute, send messages, execute."
        breadcrumbs={[{ label: 'Libraries', to: '/agents/libraries' }, { label: 'Tools' }]}
        helpContent={help}
        actions={newToolLink}
        alert={
          toolsQuery.isError ? (
            <Callout tone="danger" title="Could not load tools">
              {describeQueryError(toolsQuery.error, 'tools')}
            </Callout>
          ) : undefined
        }
        toolbar={
          <Toolbar
            search={<SearchBar value={search} onChange={setSearch} placeholder="Search name, description, category…" />}
            pills={
              <FilterPills
                aria-label="Status"
                options={pills}
                value={status}
                onChange={(id) => setStatus(id as StatusPill)}
              />
            }
            right={
              <>
                <FilterSelect
                  label="Category"
                  anyLabel="All categories"
                  value={category}
                  options={facetOptions(tools, (tool) => tool.category)}
                  onChange={setCategory}
                />
                <FilterSelect
                  label="Source"
                  anyLabel="All sources"
                  value={source}
                  options={facetOptions(
                    tools,
                    (tool) => tool.sourceLabel,
                    (value) => value,
                  )}
                  onChange={setSource}
                />
              </>
            }
          />
        }
      >
        {!toolsQuery.isError && (
          <DataTable
            aria-label="Tools"
            columns={COLUMNS}
            rows={visible}
            rowKey={(tool) => tool.name}
            loading={toolsQuery.isPending}
            pageSize={25}
            empty={
              <EmptyState
                icon={Sparkles}
                title={tools.length === 0 ? (help?.emptyState.title ?? 'No tools yet') : 'No tools match'}
                description={
                  tools.length === 0
                    ? (help?.emptyState.description ?? 'Tools appear here once they are registered.')
                    : 'Change the search or filters to see more tools.'
                }
                learnItems={tools.length === 0 ? help?.emptyState.learnItems : undefined}
                action={tools.length === 0 ? newToolLink : undefined}
              />
            }
          />
        )}
      </ListPage>

      <MarketplaceResults entityType="tool" search={search} label="tools" onSelect={setMarketplaceTarget} />
      <MarketplaceDetailSheet listing={marketplaceTarget} onClose={() => setMarketplaceTarget(null)} />
    </>
  );
};
